import { FormType } from "@simplyk/common";
import { useWatch } from "react-hook-form";

import { scrollToTop } from "../../../../helpers/utils";
import { useFormV2Context } from "../../context/FormV2Context";
import { findStep1FirstErrorPath } from "../../helpers/findFirstErrorPath";

import { AmplitudeEvents } from "@/constants/amplitude";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";

export const useTicketingFirstStepSubmit = () => {
  const { logAmplitudeEvent } = useAmplitude();
  const { form, setCurrentStep, formObject: ticketing, setValidationMode } = useFormV2Context(FormType.Ticketing);

  const { trigger, control } = form;

  const logDonorFormStepSubmitted = () => {
    logAmplitudeEvent(AmplitudeEvents.DonorFormStepSubmitted, {
      formId: ticketing.id,
      formType: ticketing.formCategory,
      organizationCountry: ticketing.organization?.country,
      location,
    });
  };

  const ticketsPurchased = useWatch({
    control,
    name: "ticketsPurchased",
  });
  const totalTickets = ticketsPurchased?.reduce((total, item) => total + (item?.purchasedNumber || 0), 0) || 0;
  const additionalDonation = useWatch({
    control,
    name: "extraDonation",
  });

  const hasPurchaseOrDonation: boolean = totalTickets > 0 || Boolean(additionalDonation);
  const canSubmitFirstStep = hasPurchaseOrDonation;

  const focusWithScroll = (element: HTMLElement) => {
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const isElementBelowHalfScreen = elementRect.bottom > window.innerHeight / 2;
      element.focus();
      if (isElementBelowHalfScreen) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  const handleErrorFocus = () => {
    const errors = form.formState.errors;
    const firstErrorFieldName = findStep1FirstErrorPath(errors);
    const firstErrorElement = firstErrorFieldName ? document.getElementsByName(firstErrorFieldName)[0] : null;

    if (firstErrorElement) {
      focusWithScroll(firstErrorElement);
    } else {
      form.trigger();
    }
  };

  const onFirstStepSubmit = async () => {
    if (!canSubmitFirstStep) {
      return;
    }

    const isValid = await trigger(
      [
        "ticketsPurchased",
        "customAnswersOfParticipants",
        "extraDonation",
        "manualExtraDonation",
        "addGiftAid",
        "hasGiftAidConfirmation",
      ],
      { shouldFocus: true }
    );

    if (isValid) {
      scrollToTop("auto");
      setCurrentStep("payment");
      logDonorFormStepSubmitted();
      setValidationMode("onBlur");
    } else {
      handleErrorFocus();
      setValidationMode("onChange");
    }
  };

  return { onFirstStepSubmit, canSubmitFirstStep, totalTickets, additionalDonation: additionalDonation || 0 };
};
